import { BreedingManager as BreedLogic } from '@/breedLogic'
import { Chocobo } from '@/models/chocobos'

export class BreedingManager {
  /**
   * Start a breeding process that takes 14 days
   * @param {Object} mother - Female chocobo
   * @param {Object} father - Male chocobo
   * @param {Object} gameStore - Game store instance
   * @returns {Object} Breeding record
   */
  static startBreeding(mother, father, gameStore) {
    // Validate breeding
    const validation = BreedLogic.validateBreeding(mother, father)
    if (!validation.valid) {
      throw new Error(validation.reason)
    }

    // Pre-generate offspring data (but don't create the chocobo yet)
    const offspringData = BreedLogic.breedChocobos(mother, father, gameStore.currentDay + 14)
    
    // Set lineage information for the offspring
    offspringData.setLineage(mother, father)
    
    // Create breeding record
    const breedingRecord = {
      id: crypto.randomUUID(),
      motherId: mother.id,
      motherName: mother.name,
      fatherId: father.id,
      fatherName: father.name,
      ownerId: gameStore.playerOwner.id,
      ownerName: gameStore.playerOwner.name,
      startDay: gameStore.currentDay,
      endDay: gameStore.currentDay + 14,
      stage: 'incubating',
      offspringData: offspringData.toJSON(), // Store as plain object
      paid: 30000 // Base breeding cost
    }

    // Mark mother as breeding
    if (typeof mother.startBreeding === 'function') {
      mother.startBreeding(father.id, father.name, gameStore.currentDay, 'mother')
    }
    
    // Mark father as breeding (but only if it's an active chocobo, not a retired breeder)
    if (typeof father.startBreeding === 'function') {
      father.startBreeding(mother.id, mother.name, gameStore.currentDay, 'father')
    }

    // Add to active breedings
    gameStore.activeBreedings.push(breedingRecord)
    
    // Update breeding statistics
    this.updateBreedingStats(gameStore, 'started', null)
    
    // Add breeding started notification
    this.addBreedingStartedNotification(gameStore, breedingRecord)

    return breedingRecord
  }

  /**
   * Process daily breeding updates
   * @param {Object} gameStore - Game store instance
   */
  static processBreedings(gameStore) {
    if (!gameStore.activeBreedings || gameStore.activeBreedings.length === 0) return

    const completedBreedings = []

    gameStore.activeBreedings.forEach(breeding => {
      const daysElapsed = gameStore.currentDay - breeding.startDay

      // Update breeding stage
      if (daysElapsed <= 6) {
        breeding.stage = 'incubating'
      } else if (daysElapsed <= 9) {
        breeding.stage = 'hatching'
      } else if (daysElapsed < 14) {
        breeding.stage = 'nursing'
      } else if (daysElapsed >= 14) {
        // Breeding complete!
        completedBreedings.push(breeding)
      }
    })

    // Process completed breedings
    completedBreedings.forEach(breeding => {
      this.completeBreeding(breeding, gameStore)
    })

    // Remove completed breedings from active list
    gameStore.activeBreedings = gameStore.activeBreedings.filter(
      breeding => !completedBreedings.includes(breeding)
    )
  }

  /**
   * Complete a breeding and add the offspring
   * @param {Object} breeding - Breeding record
   * @param {Object} gameStore - Game store instance
   */
  static completeBreeding(breeding, gameStore) {
    try {
      // Find the mother
      const mother = gameStore.chocobos.find(c => c.id === breeding.motherId)
      if (mother && typeof mother.completeBreeding === 'function') {
        mother.completeBreeding()
      }

      // Find the father (may not exist if it was a retired breeding partner)
      const father = gameStore.chocobos.find(c => c.id === breeding.fatherId)
      if (father && typeof father.completeBreeding === 'function') {
        father.completeBreeding()
      }

      // Validate offspring data
      const offspringData = breeding.offspringData
      if (!offspringData || !offspringData.color || !offspringData.region || !offspringData.name) {
        console.error('Invalid offspring data in breeding record:', breeding.id)
        return
      }
      
      // Create a new Chocobo instance and copy the stored data
      const offspring = new Chocobo(offspringData.color, offspringData.region, offspringData.name)
      
      // Copy all the breeding-generated properties
      Object.keys(offspringData).forEach(key => {
        if (key !== 'color' && key !== 'region' && key !== 'name') {
          offspring[key] = offspringData[key]
        }
      })
      
      // Ensure total stats are updated
      offspring.updateTotalStats()

      // Add to appropriate owner's stable
      const owner = gameStore.owners.find(o => o.id === breeding.ownerId)
      if (owner) {
        gameStore.chocobos.push(offspring)
        owner.chocoboIds.push(offspring.id)

        // Add notification if player
        if (owner.isPlayer) {
          this.addBreedingCompleteNotification(gameStore, breeding, offspring)
        }
      }

      // Move to completed breedings history
      gameStore.completedBreedings.unshift({
        ...breeding,
        completedDay: gameStore.currentDay,
        offspringId: offspring.id,
        offspringName: offspring.name
      })

      // Update breeding statistics
      this.updateBreedingStats(gameStore, 'completed', offspring)

      // Keep only last 50 completed breedings
      if (gameStore.completedBreedings.length > 50) {
        gameStore.completedBreedings = gameStore.completedBreedings.slice(0, 50)
      }
      
    } catch (error) {
      console.error('Failed to complete breeding:', breeding.id, error)
      
      // Still need to free the parent chocobos even if offspring creation fails
      const mother = gameStore.chocobos.find(c => c.id === breeding.motherId)
      if (mother && typeof mother.completeBreeding === 'function') {
        mother.completeBreeding()
      }
      const father = gameStore.chocobos.find(c => c.id === breeding.fatherId)
      if (father && typeof father.completeBreeding === 'function') {
        father.completeBreeding()
      }
    }
  }

  /**
   * Add breeding complete notification
   * @param {Object} gameStore - Game store instance
   * @param {Object} breeding - Breeding record
   * @param {Object} offspring - New chocobo
   */
  static addBreedingCompleteNotification(gameStore, breeding, offspring) {
    if (!gameStore.notifications) {
      gameStore.notifications = []
    }

    gameStore.notifications.unshift({
      id: crypto.randomUUID(),
      type: 'breeding_complete',
      title: 'New Chocobo Born!',
      message: `${offspring.name} has been born! ${breeding.motherName} is now available for racing again.`,
      day: gameStore.currentDay,
      read: false,
      chocoboId: offspring.id,
      timestamp: Date.now()
    })

    // Keep only last 50 notifications
    if (gameStore.notifications.length > 50) {
      gameStore.notifications = gameStore.notifications.slice(0, 50)
    }
  }
  
  /**
   * Add breeding started notification
   * @param {Object} gameStore - Game store instance
   * @param {Object} breeding - Breeding record
   */
  static addBreedingStartedNotification(gameStore, breeding) {
    if (!gameStore.notifications) {
      gameStore.notifications = []
    }

    gameStore.notifications.unshift({
      id: crypto.randomUUID(),
      type: 'breeding_started',
      title: 'Breeding Started',
      message: `${breeding.motherName} and ${breeding.fatherName} have begun breeding. The chick will be born in 14 days.`,
      day: gameStore.currentDay,
      read: false,
      motherId: breeding.motherId,
      timestamp: Date.now()
    })

    // Keep only last 50 notifications
    if (gameStore.notifications.length > 50) {
      gameStore.notifications = gameStore.notifications.slice(0, 50)
    }
  }

  /**
   * Check if a chocobo is available for racing/training
   * @param {Object} chocobo - Chocobo to check
   * @returns {boolean} True if available
   */
  static isChocoboAvailable(chocobo) {
    // Males are always available
    if (chocobo.gender === 'male') return true
    
    // Females are unavailable when breeding
    return !chocobo.isBreeding
  }

  /**
   * Update breeding statistics
   * @param {Object} gameStore - Game store instance
   * @param {string} action - 'started' or 'completed'
   * @param {Object} offspring - New chocobo (for completed breedings)
   * @param {Object} breeding - Breeding record (for started breedings)
   */
  static updateBreedingStats(gameStore, action, offspring = null) {
    if (!gameStore.breedingStats) return

    // Reset monthly stats if new month
    const currentMonth = Math.floor((gameStore.currentDay - 1) / 32) + 1
    if (currentMonth !== gameStore.breedingStats.currentMonth) {
      gameStore.breedingStats.monthlyBreedings = 0
      gameStore.breedingStats.currentMonth = currentMonth
    }

    if (action === 'started') {
      gameStore.breedingStats.totalBreedings++
      gameStore.breedingStats.monthlyBreedings++
    } else if (action === 'completed' && offspring) {
      gameStore.breedingStats.successfulBreedings++
      
      // Update color breakdown
      const color = offspring.color
      if (!gameStore.breedingStats.colorBreakdown[color]) {
        gameStore.breedingStats.colorBreakdown[color] = 0
      }
      gameStore.breedingStats.colorBreakdown[color]++
      
      // Update best offspring stats
      const totalStats = offspring.speed + offspring.stamina
      if (!gameStore.breedingStats.bestOffspringStats || 
          totalStats > gameStore.breedingStats.bestOffspringStats.totalStats) {
        gameStore.breedingStats.bestOffspringStats = {
          name: offspring.name,
          color: offspring.color,
          speed: offspring.speed,
          stamina: offspring.stamina,
          totalStats: totalStats,
          level: offspring.level,
          day: gameStore.currentDay
        }
      }
      
      // Calculate average offspring level
      const completedCount = gameStore.breedingStats.successfulBreedings
      const currentAvg = gameStore.breedingStats.averageOffspringLevel
      gameStore.breedingStats.averageOffspringLevel = 
        ((currentAvg * (completedCount - 1)) + offspring.level) / completedCount
    }
  }

  /**
   * Get breeding stage display info
   * @param {string} stage - Breeding stage
   * @returns {Object} Stage display info
   */
  static getStageDisplayInfo(stage) {
    const stages = {
      incubating: {
        icon: '🥚',
        text: 'Incubating',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-500'
      },
      hatching: {
        icon: '🐣',
        text: 'Hatching',
        color: 'bg-orange-100 text-orange-800 border-orange-500'
      },
      nursing: {
        icon: '🐤',
        text: 'Nursing',
        color: 'bg-green-100 text-green-800 border-green-500'
      }
    }

    return stages[stage] || stages.incubating
  }

  /**
   * Fix stuck breeding records (utility for save repair)
   * @param {Object} gameStore - Game store instance
   * @returns {number} Number of stuck breedings fixed
   */
  static fixStuckBreedings(gameStore) {
    if (!gameStore.activeBreedings || gameStore.activeBreedings.length === 0) {
      return 0
    }

    let fixedCount = 0
    const stuckBreedings = []

    // Find breedings that should have completed by now
    gameStore.activeBreedings.forEach(breeding => {
      const daysElapsed = gameStore.currentDay - breeding.startDay
      if (daysElapsed >= 14) {
        stuckBreedings.push(breeding)
      }
    })

    // Force complete the stuck breedings
    stuckBreedings.forEach(breeding => {
      this.completeBreeding(breeding, gameStore)
      fixedCount++
    })

    // Remove stuck breedings from active list
    gameStore.activeBreedings = gameStore.activeBreedings.filter(
      breeding => !stuckBreedings.includes(breeding)
    )

    return fixedCount
  }
}